import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Button,
  Stack,
} from '@mui/material';
import Link from '@mui/material/Link';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { buildUtmLink, getUtmDebugAttributes } from '../utils/utm';
import { FIELD_BLACK, PAPER_DARK, DEEP_BLACK, SIGNAL_ORANGE, SIGNAL_ORANGE_BORDER } from '../design/stoneTokens';
import { useAuth } from '../context/AuthContext';
import {
  ensureHomeFirst,
  getAuthLinks,
  getPrimaryNavigationLinks,
} from '../utils/navigation';
import { useNavigationGuard } from '../context/NavigationGuardContext';
import { isIsolatedSurface } from '../utils/routeSurfaces';

const pillStyles = (theme) => ({
  color: '#fff',
  marginRight: theme.spacing(1.5),
  textDecoration: 'none',
  fontSize: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 999,
  border: `1px solid ${SIGNAL_ORANGE_BORDER}`,
  background: PAPER_DARK,
  boxShadow: '0 8px 20px -12px rgba(15,23,42,0.7)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  '&:hover': {
    textDecoration: 'none',
    transform: 'translateY(-1px)',
    borderColor: SIGNAL_ORANGE,
    boxShadow: '0 12px 28px -14px rgba(15,23,42,0.8)',
  },
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(1),
    fontSize: '0.9rem',
    padding: theme.spacing(0.6, 1.25),
  },
});

const NavLink = styled(Link)(({ theme }) => ({
  ...pillStyles(theme),
}));

const PillButton = styled(Button)(({ theme }) => ({
  ...pillStyles(theme),
  textTransform: 'none',
  minWidth: 0,
}));

const DesktopNavLinks = ({ navLinks, overflowLinks, onMoreOpen, moreAnchorEl }) => {
  const location = useLocation();
  return (
    <Box display="flex" justifyContent="center" mt={2} flexWrap="wrap">
      {navLinks.map((link) => (
        <NavLink
          key={link.href}
          component={RouterLink}
          to={link.to}
          sx={{ maxWidth: 180 }}
          {...getUtmDebugAttributes(location, { campaign: link.utmCampaign || 'nav' })}
        >
          {link.label}
        </NavLink>
      ))}
      {overflowLinks.length > 0 && (
        <>
          <PillButton
            id="footer-more-menu-button"
            type="button"
            aria-haspopup="true"
            aria-expanded={Boolean(moreAnchorEl)}
            aria-controls={Boolean(moreAnchorEl) ? 'footer-more-menu' : undefined}
            onClick={onMoreOpen}
          >
            More
          </PillButton>
          <Menu
            id="footer-more-menu"
            anchorEl={moreAnchorEl}
            open={Boolean(moreAnchorEl)}
            onClose={() => onMoreOpen(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            MenuListProps={{ 'aria-labelledby': 'footer-more-menu-button' }}
          >
            {overflowLinks.map((link) => (
              <MenuItem
                key={link.href}
                component={RouterLink}
                to={link.to}
                onClick={() => onMoreOpen(null)}
                {...getUtmDebugAttributes(location, { campaign: link.utmCampaign || 'nav' })}
              >
                {link.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

const MobileNavLinks = ({ navLinks, handleDrawerToggle }) => {
  const location = useLocation();
  return (
    <Box role="presentation">
      <List sx={{ px: 2, py: 1 }}>
        {navLinks.map((link) => (
          <ListItem
            button
            key={link.href}
            component={RouterLink}
            to={link.to}
            onClick={handleDrawerToggle}
            sx={{
              my: 0.75,
              borderRadius: 2,
              border: `1px solid ${SIGNAL_ORANGE_BORDER}`,
              background: PAPER_DARK,
              '&:hover': { borderColor: SIGNAL_ORANGE },
            }}
            {...getUtmDebugAttributes(location, { campaign: link.utmCampaign || 'nav' })}
          >
            <ListItemText primary={link.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

function FooterInner() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { user } = useAuth();
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();
  const { isDirty } = useNavigationGuard();

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleMoreOpen = (eventOrNull) => {
    if (!eventOrNull) {
      setMoreAnchorEl(null);
      return;
    }
    setMoreAnchorEl(eventOrNull.currentTarget);
  };

  const navLinks = useMemo(
    () => getPrimaryNavigationLinks({ location, user }),
    [location, user],
  );

  const orderedNavLinks = useMemo(() => ensureHomeFirst(navLinks), [navLinks]);

  const desktopLinks = useMemo(() => {
    const visibleCount = isLargeScreen ? orderedNavLinks.length : Math.min(4, orderedNavLinks.length);
    return {
      primary: orderedNavLinks.slice(0, visibleCount),
      overflow: orderedNavLinks.slice(visibleCount),
    };
  }, [orderedNavLinks, isLargeScreen]);

  const authLinks = useMemo(
    () => getAuthLinks(location, { signupCampaign: 'footer_signup', loginCampaign: 'footer_login' }),
    [location],
  );

  const homeLink = useMemo(() => {
    const homeNavLink = orderedNavLinks.find((link) => link.href === '/' && !link.anchor);
    return homeNavLink ? homeNavLink.to : buildUtmLink({ pathname: '/', location, utmCampaign: 'nav_home' });
  }, [orderedNavLinks, location]);

  useEffect(() => {
    if (!isDesktop || desktopLinks.overflow.length === 0) {
      setMoreAnchorEl(null);
    }
  }, [isDesktop, desktopLinks.overflow.length]);

  const focusableSelectors = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    if (mobileOpen && drawerRef.current) {
      const focusable = drawerRef.current.querySelectorAll(focusableSelectors);
      if (focusable.length) {
        focusable[0].focus();
      }
    } else if (!mobileOpen && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [mobileOpen, focusableSelectors]);

  const handleDrawerKeyDown = (event) => {
    if (event.key !== 'Tab' || !drawerRef.current) return;
    const focusable = drawerRef.current.querySelectorAll(focusableSelectors);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: FIELD_BLACK,
        color: 'white',
        mt: 8,
        pt: 3,
        pb: 2,
        position: 'relative',
        zIndex: 5,
        borderTop: `1px solid ${SIGNAL_ORANGE_BORDER}`,
      }}
    >
      {isDirty && (
        <Box sx={{
          px: 2,
          py: 0.75,
          background: PAPER_DARK,
          borderBottom: `1px solid ${SIGNAL_ORANGE_BORDER}`,
        }}>
          <Typography variant="caption" color="warning.main">
            Unsaved changes — navigating away will prompt.
          </Typography>
        </Box>
      )}
      <Container maxWidth="md">
        <Typography
          variant="h6"
          component={RouterLink}
          to={homeLink}
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'inherit',
            textDecoration: 'none',
            fontSize: { xs: '1.5rem', sm: '1.8rem' },
          }}
          {...getUtmDebugAttributes(location, { campaign: 'nav_home' })}
        >
          Togeevision
        </Typography>

        {!user && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            mt={3}
          >
            <PillButton
              component={RouterLink}
              to={authLinks.signup}
              sx={{
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 3,
              }}
              {...getUtmDebugAttributes(location, { campaign: 'footer_signup' })}
            >
              Create free account
            </PillButton>
            <PillButton
              component={RouterLink}
              to={authLinks.login}
              sx={{
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                px: 3,
                borderColor: SIGNAL_ORANGE_BORDER,
              }}
              {...getUtmDebugAttributes(location, { campaign: 'footer_login' })}
            >
              Log in
            </PillButton>
          </Stack>
        )}

        {isDesktop ? (
          <DesktopNavLinks
            navLinks={desktopLinks.primary}
            overflowLinks={desktopLinks.overflow}
            onMoreOpen={handleMoreOpen}
            moreAnchorEl={moreAnchorEl}
          />
        ) : (
          <Box display="flex" justifyContent="center" mt={2}>
            <IconButton
              color="inherit"
              aria-label="Open footer navigation"
              aria-haspopup="true"
              aria-expanded={mobileOpen}
              aria-controls="footer-drawer"
              edge="end"
              onClick={handleDrawerToggle}
              ref={triggerRef}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Drawer
          anchor="bottom"
          id="footer-drawer"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
            'aria-labelledby': 'footer-drawer-title',
            'aria-modal': true,
          }}
          PaperProps={{
            role: 'dialog',
            'aria-labelledby': 'footer-drawer-title',
            'aria-describedby': 'footer-drawer-description',
            'aria-modal': true,
            ref: drawerRef,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              background: DEEP_BLACK,
              color: '#fff',
              borderTop: `1px solid ${SIGNAL_ORANGE_BORDER}`,
            },
          }}
          onKeyDown={handleDrawerKeyDown}
          aria-label="Site navigation"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: theme.spacing(0, 1),
              ...theme.mixins.toolbar,
              justifyContent: 'space-between',
            }}
          >
            <Typography id="footer-drawer-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
              Navigation menu
            </Typography>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ color: '#fff' }}
              aria-label="Close footer navigation"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Typography id="footer-drawer-description" sx={{ px: 2, py: 1, color: 'rgba(226,232,240,0.8)' }}>
            Links are grouped for easier browsing on mobile screens.
          </Typography>
          <MobileNavLinks
            navLinks={orderedNavLinks}
            handleDrawerToggle={handleDrawerToggle}
          />
        </Drawer>

        <Typography variant="body2" align="center" sx={{ opacity: 0.85, mt: 4, mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          TogeeVision is a decentralized transmedia network for stories anchored to place.
          <br />
          <strong>Delano Franklin — Founder &amp; Creative Director, TogeeVision (Host, The Black Star Signal)</strong>
        </Typography>
        <Typography
          variant="body2"
          align="center"
          mt={2}
          sx={{
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
          }}
        >
          © {new Date().getFullYear()} Togeevision. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

// Wrapper checks location BEFORE FooterInner mounts so no hooks are skipped.
function Footer() {
  const location = useLocation();
  if (isIsolatedSurface(location.pathname)) return null;
  return <FooterInner />;
}

export default Footer;
